import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Button,
  Checkbox,
  Form,
  Input,
  Select,
  Dropdown,
  Menu,
  Row,
  Col,
  Collapse,
  Card,
  Table,
} from "antd";
import RightBar from "../../../components/common/RightBar";

import { propertyData } from "../../../data/properties";
import { DownOutlined } from "@ant-design/icons";
import { defaultColumns, data, BudgetRightBar } from "./BudgetData";

const EditableContext = React.createContext(null);
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};
const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);
  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };
  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const Budget = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { Panel } = Collapse;
  const { Search } = Input;

  const { id } = useParams();

  function findDataById(id) {
    return propertyData.find((item) => item.id === id);
  }

  const dataObject = findDataById(id);
  const hasSelected = selectedRowKeys.length > 0;

  const menu1 = (
    <Menu>
      <Menu.Item key="Fill with Prior Budget" disabled={hasSelected?false:true}>Fill with Prior Budget</Menu.Item>
      <Menu.Item key="Fill with Year-To-Date Actuals"  disabled={hasSelected?false:true}>
        Fill with Year-To-Date Actuals
      </Menu.Item>
      <Menu.Item key="Fill with Trailing 12-month Actuals"  disabled={hasSelected?false:true}>
        Fill with Trailing 12-month Actuals
      </Menu.Item>
    </Menu>
  );

  const menu2 = (
    <Menu>
      <Menu.Item key="Update by Percentage"  disabled={hasSelected?false:true}>Update by Percentage</Menu.Item>
      <Menu.Item key="Update by Dollar"  disabled={hasSelected?false:true}>Update by Dollar</Menu.Item>
    </Menu>
  );

  const onSelectChange = (newSelectedRowKeys) => {
    console.log("selectedRowKeys changed: ", newSelectedRowKeys);
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const [dataSource, setDataSource] = useState(data);

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div>
      <div className="pageheading ">
        <h1>Budget</h1>
        <div className="pageHeadingBtns">
          <Button>
            <i
              className="fa fa-expand fa-fw pe-4"
              style={{ marginRight: "5px" }}
            ></i>{" "}
            Full Screen View
          </Button>
          <Button>Import CSV</Button>
          <Button>Export CSV</Button>
        </div>
      </div>
      <h4>{dataObject.aboutProperty.PropertyName}</h4>
      <Form
        name="basic"
        labelCol={{
          span: 8,
        }}
        initialValues={{
          remember: true,
        }}
        autoComplete="off"
        colon={false}
      >
        <Form.Item label="Fiscal Year">
          <Select
            showSearch
            allowClear
            defaultValue="Jan 2023 - Dec 2023"
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            options={[
              {
                value: "Jan 2023 - Dec 2023",
              },
              {
                value: "Jan 2022 - Dec 2022",
              },
              {
                value: "Jan 2021 - Dec 2021",
              },
            ]}
          />
        </Form.Item>
        <Form.Item label="Description">
          <Input />
        </Form.Item>
        <Form.Item label=" ">
          <Checkbox>
            Exclude GL Accounts without budget values or notes
          </Checkbox>
        </Form.Item>
      </Form>
      <Row justify="space-between">
        <Col space={18}>
          <Button.Group>
            <Button disabled>Bulk Actions</Button>
            <Dropdown overlay={menu1}>
              <Button>
                Fill <DownOutlined />
              </Button>
            </Dropdown>
            <Dropdown overlay={menu2}>
              <Button>
                Update <DownOutlined />
              </Button>
            </Dropdown>
            <Button  disabled={hasSelected?false:true}>Divide Total Across 12 Months</Button>
            <Button  disabled={hasSelected?false:true}>Show Actuals Comparison</Button>
            <Button  disabled={hasSelected?false:true}>Clear Values</Button>
          </Button.Group>
        </Col>
        <Col space={6}>
          <Search
            placeholder="Search GL Accounts"
            style={{
              width: '100%',
            }}
          />
        </Col>
      </Row>
      <br />

      <Table
        components={components}
        rowClassName={() => "editable-row"}
        dataSource={dataSource}
        columns={columns}
        scroll={{ x: 1600 }}
        rowSelection={rowSelection}
      />
      <Row gutter={16}>
        <Col>
          <Button type="primary">Save</Button>
        </Col>
        <Col>
          <Button>Cancel</Button>
        </Col>
      </Row>
      <br />

      <Collapse>
        <Panel
          header="Audit Log"
          key="1"
          extra={
            <Button>
              <i
                aria-hidden="true"
                className="fa fa-envelope me-2"
                style={{ marginRight: "5px" }}
              ></i>{" "}
              FEEDBACK
            </Button>
          }
        >
          <Card style={{ textAlign: "center" }}>
            <p>There is no activity to display.</p>
          </Card>
        </Panel>
      </Collapse>

      <div className="RightSidebar">
        <RightBar RightBarData={BudgetRightBar} />
      </div>
    </div>
  );
};

export default Budget;
